import {
  Button,
  DatePicker,
  Input,
  Popover,
  PopoverContent,
  PopoverTrigger,
  useDebounceCallback,
} from "@devhop/ui";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { format } from "date-fns";
import { ChevronDownIcon, XIcon } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import type { FilterConditionDTO } from "@/shared/contracts/base";
import {
  findCondition,
  removeConditions,
  upsertCondition,
} from "@/shared/contracts/query-helpers";
import { config } from "@/shared/lib/config";
import { fetchLookupForInfinite, hydrateLookupItem } from "@/shared/lib/utils";
import { InfiniteCombobox } from "@/shared/ui/InfiniteCombobox";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";

type StudentLookupItem = { id: string; name: string; memberId: string };

function findDateConditions(filters: FilterConditionDTO[] | undefined): {
  from?: string;
  to?: string;
} {
  if (!filters) return {};
  let from: string | undefined;
  let to: string | undefined;
  for (const item of filters) {
    if (item.op === "and" && Array.isArray(item.value)) {
      for (const c of item.value as FilterConditionDTO[]) {
        if (c.field === "issuedAt" && typeof c.value === "string") {
          if (c.op === "gte") from = c.value.slice(0, 10);
          if (c.op === "lte") to = c.value.slice(0, 10);
        }
      }
    }
  }
  return { from, to };
}

function findAmountConditions(filters: FilterConditionDTO[] | undefined): {
  min?: string;
  max?: string;
} {
  if (!filters) return {};
  let min: string | undefined;
  let max: string | undefined;
  for (const item of filters) {
    if (item.field === "amount" && item.op === "and" && Array.isArray(item.value)) {
      for (const c of item.value as FilterConditionDTO[]) {
        if (c.field !== "amount" || typeof c.value !== "number") continue;
        if (c.op === "gte") min = String(c.value);
        if (c.op === "lte") max = String(c.value);
      }
    } else if (item.field === "amount" && typeof item.value === "number") {
      if (item.op === "gte") min = String(item.value);
      if (item.op === "lte") max = String(item.value);
    }
  }
  return { min, max };
}

function parseAmountInput(raw: string): number | undefined {
  const normalized = raw.replace(/,/g, "").trim();
  if (!normalized) return undefined;
  const n = Number(normalized);
  if (!Number.isFinite(n) || n < 0) return undefined;
  return Math.round(n);
}

function formatAmountLabel(min?: string, max?: string): string {
  const fmt = (raw: string) => {
    const n = parseAmountInput(raw);
    return n === undefined ? raw : n.toLocaleString("lo-LA");
  };
  if (min && max) return `${fmt(min)} - ${fmt(max)} ກີບ`;
  if (min) return `≥ ${fmt(min)} ກີບ`;
  if (max) return `≤ ${fmt(max)} ກີບ`;
  return "ຍອດເງິນ";
}

export function ReceiptsFilter() {
  const nav = useNavigate({ from: "/app/billing/receipts" });
  const search = useSearch({ from: "/app/billing/receipts" }) as Record<
    string,
    unknown
  >;
  const filters = (search.filters as FilterConditionDTO[] | undefined) ?? [];
  const inputRef = useRef<HTMLInputElement>(null);
  const contactNameInputRef = useRef<HTMLInputElement>(null);

  const receiptNoFilter = findCondition(filters, "receiptNo");
  const hasDiscountFilter = findCondition(filters, "discountAmount");
  const studentFilter = findCondition(filters, "studentId");
  const billTypeFilter =
    findCondition(filters, "billType") ?? findCondition(filters, "itemType");
  const contactNameFilter =
    findCondition(filters, "contactName") ??
    findCondition(filters, "bookerName");
  const studentIdValue =
    typeof studentFilter?.value === "string" ? studentFilter.value : "";
  const billTypeValue =
    typeof billTypeFilter?.value === "string" ? billTypeFilter.value : "all";
  const contactNameValue =
    typeof contactNameFilter?.value === "string"
      ? contactNameFilter.value
      : "";
  const dateRange = findDateConditions(filters);
  const amountRange = findAmountConditions(filters);
  const isRoomBooking = billTypeValue === "room_booking";

  const [searchValue, setSearchValue] = useState<string>("");
  const [contactNameSearchValue, setContactNameSearchValue] =
    useState<string>("");
  const [fromDate, setFromDate] = useState<string>(dateRange.from ?? "");
  const [toDate, setToDate] = useState<string>(dateRange.to ?? "");
  const [amountOpen, setAmountOpen] = useState(false);
  const [draftAmountMin, setDraftAmountMin] = useState<string>(
    amountRange.min ?? "",
  );
  const [draftAmountMax, setDraftAmountMax] = useState<string>(
    amountRange.max ?? "",
  );

  useEffect(() => {
    setSearchValue((receiptNoFilter?.value as string) ?? "");
  }, [receiptNoFilter?.value]);

  useEffect(() => {
    setContactNameSearchValue(contactNameValue);
    if (contactNameInputRef.current) {
      contactNameInputRef.current.value = contactNameValue;
    }
  }, [contactNameValue]);

  useEffect(() => {
    setFromDate(dateRange.from ?? "");
    setToDate(dateRange.to ?? "");
  }, [dateRange.from, dateRange.to]);

  const debounced = useDebounceCallback((val: string) => {
    setSearchValue(val);
    const nextFilters = val
      ? upsertCondition(filters, {
          field: "receiptNo",
          op: "contains",
          value: val,
        })
      : removeConditions(filters, "receiptNo");
    nav({
      search: {
        ...search,
        offset: 0,
        filters: nextFilters?.length ? nextFilters : undefined,
      },
    });
  }, 400);

  const debouncedContactName = useDebounceCallback((val: string) => {
    setContactNameSearchValue(val);
    let nextFilters =
      removeConditions(
        removeConditions(filters, "contactName"),
        "bookerName",
      ) ?? [];
    const trimmed = val.trim();
    if (trimmed) {
      nextFilters = upsertCondition(nextFilters, {
        field: "contactName",
        op: "contains",
        value: trimmed,
      });
    }
    nav({
      search: {
        ...search,
        offset: 0,
        filters: nextFilters?.length ? nextFilters : undefined,
      },
    });
  }, 400);

  const applyAmountFilter = (minRaw: string, maxRaw: string) => {
    let min = parseAmountInput(minRaw);
    let max = parseAmountInput(maxRaw);
    if (min !== undefined && max !== undefined && min > max) {
      const swap = min;
      min = max;
      max = swap;
    }

    let nextFilters: FilterConditionDTO[] =
      removeConditions(filters, "amount") ?? [];
    const amountConditions: FilterConditionDTO[] = [];
    if (min !== undefined) {
      amountConditions.push({
        field: "amount",
        op: "gte",
        value: min,
      });
    }
    if (max !== undefined) {
      amountConditions.push({
        field: "amount",
        op: "lte",
        value: max,
      });
    }
    if (amountConditions.length > 0) {
      nextFilters = upsertCondition(nextFilters, {
        field: "amount",
        op: "and",
        value: amountConditions,
      } as FilterConditionDTO);
    }

    nav({
      search: {
        ...search,
        offset: 0,
        filters: nextFilters?.length ? nextFilters : undefined,
      },
    });
    setAmountOpen(false);
  };

  const hasAmountFilter = !!amountRange.min || !!amountRange.max;
  const hasFilter =
    !!receiptNoFilter?.value ||
    !!hasDiscountFilter?.value ||
    !!studentIdValue ||
    billTypeValue !== "all" ||
    !!contactNameValue ||
    !!fromDate ||
    !!toDate ||
    hasAmountFilter;

  return (
    <div className="mb-4 flex flex-col gap-2 px-2">
      <div className="flex items-center justify-between gap-4 max-sm:flex-col">
        <div className="flex flex-1 flex-wrap items-center gap-2">
          <Input
            ref={inputRef}
            placeholder="ຄົ້ນຫາເລກທີ..."
            defaultValue={searchValue}
            onChange={(e) => debounced(e.target.value)}
            className="h-8 sm:max-w-2xs"
          />

          <InfiniteCombobox<StudentLookupItem>
            queryKey={["students", "lookup"]}
            queryFn={(args) =>
              fetchLookupForInfinite<StudentLookupItem>(
                `${config.apiUrl}/students/lookup`,
                args,
              )
            }
            preloadQueryFn={(id) =>
              hydrateLookupItem<StudentLookupItem>(
                `${config.apiUrl}/students/lookup`,
                id,
              )
            }
            getLabel={(item) => `${item.memberId} - ${item.name}`}
            getValue={(item) => item.id}
            value={studentIdValue || ""}
            onValueChange={(val) => {
              let nextFilters: FilterConditionDTO[] | undefined = filters;
              if (!val || val === "") {
                nextFilters = removeConditions(filters, "studentId");
              } else {
                nextFilters = upsertCondition(filters, {
                  field: "studentId",
                  op: "eq",
                  value: val,
                });
              }
              nav({
                search: {
                  ...search,
                  offset: 0,
                  filters: nextFilters?.length ? nextFilters : undefined,
                },
              });
            }}
            placeholder="ນັກຮຽນ"
            className="!h-8 w-full min-w-0 sm:max-w-xs"
            clearable
          />

          <SimpleSelect
            value={billTypeValue || "all"}
            onValueChange={(val) => {
              let nextFilters =
                removeConditions(
                  removeConditions(filters, "billType"),
                  "itemType",
                ) ?? [];
              nextFilters =
                removeConditions(
                  removeConditions(nextFilters, "contactName"),
                  "bookerName",
                ) ?? [];
              if (val && val !== "all") {
                nextFilters = upsertCondition(nextFilters, {
                  field: "billType",
                  op: "eq",
                  value: val,
                });
              }
              setContactNameSearchValue("");
              if (contactNameInputRef.current) {
                contactNameInputRef.current.value = "";
              }
              nav({
                search: {
                  ...search,
                  offset: 0,
                  filters: nextFilters?.length ? nextFilters : undefined,
                },
              });
            }}
            placeholder="ປະເພດໃບ"
            className="!h-8 w-full sm:w-44"
            options={[
              { value: "all", label: "ປະເພດໃບທັງໝົດ" },
              { value: "package_purchase", label: "ຄລາດສ໌ທົ່ວໄປ" },
              { value: "private_lesson_package", label: "ຮຽນສ່ວນຕົວ" },
              { value: "room_booking", label: "ຈອງຫ້ອງ" },
              { value: "partner_project_sale", label: "Partner" },
              { value: "performance_document", label: "ງານສະແດງ" },
            ]}
          />

          {isRoomBooking && (
            <Input
              key={`contact-name-${billTypeValue}`}
              ref={contactNameInputRef}
              placeholder="ຄົ້ນຫາຊື່ຜູ້ຈອງ..."
              defaultValue={contactNameSearchValue}
              onChange={(e) => debouncedContactName(e.target.value)}
              className="h-8 w-full sm:max-w-xs"
            />
          )}

          <SimpleSelect
            value={
              hasDiscountFilter?.op === "gt" && hasDiscountFilter?.value === 0
                ? "has"
                : hasDiscountFilter?.op === "isNull" ||
                    hasDiscountFilter?.op === "or"
                  ? "none"
                  : "all"
            }
            onValueChange={(val) => {
              let nextFilters: FilterConditionDTO[] | undefined = filters;
              if (val === "all") {
                nextFilters = removeConditions(filters, "discountAmount");
              } else if (val === "has") {
                nextFilters = upsertCondition(filters, {
                  field: "discountAmount",
                  op: "gt",
                  value: 0,
                });
              } else {
                nextFilters = upsertCondition(filters, {
                  field: "discountAmount",
                  op: "or",
                  value: [
                    {
                      field: "discountAmount",
                      op: "isNull",
                      value: undefined,
                    },
                    { field: "discountAmount", op: "eq", value: 0 },
                  ],
                });
              }
              nav({
                search: {
                  ...search,
                  offset: 0,
                  filters: nextFilters?.length ? nextFilters : undefined,
                },
              });
            }}
            placeholder="ສ່ວນຫຼຸດ"
            className="!h-8 w-full sm:w-36"
            options={[
              { value: "all", label: "ທຸກລາຍການ" },
              { value: "has", label: "ມີສ່ວນຫຼຸດ" },
              { value: "none", label: "ບໍ່ມີສ່ວນຫຼຸດ" },
            ]}
          />

          <DatePicker
            mode="range"
            value={{
              from: fromDate ? new Date(fromDate) : undefined,
              to: toDate ? new Date(toDate) : undefined,
            }}
            onChange={(range) => {
              const from: Date | undefined = Array.isArray(range)
                ? range[0]
                : (range as { from?: Date; to?: Date })?.from;
              const to: Date | undefined = Array.isArray(range)
                ? range[1]
                : (range as { from?: Date; to?: Date })?.to;
              const fromStr = from ? format(from, "yyyy-MM-dd") : "";
              const toStr = to ? format(to, "yyyy-MM-dd") : "";
              setFromDate(fromStr);
              setToDate(toStr);

              let nextFilters: FilterConditionDTO[] =
                removeConditions(filters, "issuedAt") ?? [];
              const dateConditions: FilterConditionDTO[] = [];
              if (fromStr) {
                dateConditions.push({
                  field: "issuedAt",
                  op: "gte",
                  value: `${fromStr} 00:00:00`,
                });
              }
              if (toStr) {
                dateConditions.push({
                  field: "issuedAt",
                  op: "lte",
                  value: `${toStr} 23:59:59`,
                });
              }
              if (dateConditions.length > 0) {
                nextFilters = upsertCondition(nextFilters, {
                  field: "issuedAt",
                  op: "and",
                  value: dateConditions,
                } as FilterConditionDTO);
              }
              nav({
                search: {
                  ...search,
                  offset: 0,
                  filters: nextFilters?.length ? nextFilters : undefined,
                },
              });
            }}
            placeholder="ຊ່ວງວັນທີ"
            className="h-8 w-full sm:w-fit"
          />

          <Popover
            open={amountOpen}
            onOpenChange={(open) => {
              setAmountOpen(open);
              if (open) {
                setDraftAmountMin(amountRange.min ?? "");
                setDraftAmountMax(amountRange.max ?? "");
              }
            }}
          >
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="h-8 w-full justify-between gap-2 font-normal sm:w-auto sm:min-w-36"
              >
                <span
                  className={
                    hasAmountFilter ? "text-foreground" : "text-muted-foreground"
                  }
                >
                  {formatAmountLabel(amountRange.min, amountRange.max)}
                </span>
                <ChevronDownIcon className="size-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent align="start" className="w-72 space-y-3 p-3">
              <div className="space-y-1.5">
                <p className="font-medium text-sm">ຍອດເງິນ (ກີບ)</p>
                <div className="flex items-center gap-1.5">
                  <Input
                    type="number"
                    inputMode="numeric"
                    min={0}
                    step={1}
                    placeholder="ຂັ້ນຕ່ຳ"
                    value={draftAmountMin}
                    onChange={(e) => setDraftAmountMin(e.target.value)}
                    className="h-8"
                  />
                  <span className="shrink-0 text-muted-foreground text-xs">
                    -
                  </span>
                  <Input
                    type="number"
                    inputMode="numeric"
                    min={0}
                    step={1}
                    placeholder="ສູງສຸດ"
                    value={draftAmountMax}
                    onChange={(e) => setDraftAmountMax(e.target.value)}
                    className="h-8"
                  />
                </div>
              </div>
              <div className="flex items-center justify-between gap-2">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  disabled={!draftAmountMin && !draftAmountMax && !hasAmountFilter}
                  onClick={() => {
                    setDraftAmountMin("");
                    setDraftAmountMax("");
                    applyAmountFilter("", "");
                  }}
                >
                  ລ້າງ
                </Button>
                <Button
                  type="button"
                  size="sm"
                  onClick={() =>
                    applyAmountFilter(draftAmountMin, draftAmountMax)
                  }
                >
                  ຢືນຢັນ
                </Button>
              </div>
            </PopoverContent>
          </Popover>
        </div>

        {hasFilter && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              if (inputRef.current) inputRef.current.value = "";
              if (contactNameInputRef.current) {
                contactNameInputRef.current.value = "";
              }
              setContactNameSearchValue("");
              setDraftAmountMin("");
              setDraftAmountMax("");
              nav({ search: { ...search, filters: undefined, offset: 0 } });
            }}
            className="max-sm:w-full"
          >
            <XIcon className="size-4" />
            ລ້າງ
          </Button>
        )}
      </div>
    </div>
  );
}
